import { useMemo, useState } from "react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Empty } from "./common";
import { RdvItem } from "./RdvItem";
import { isOverdue } from "../lib/constants";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const key = (d) => format(d, "yyyy-MM-dd");

const pillClass = (r) => {
  if (r.fait) return "bg-[var(--kc-sand)] text-[var(--kc-muted)] line-through";
  if (isOverdue(r.date)) return "bg-[#B42318]/10 text-[#B42318]";
  return r.type === "relance" ? "bg-[var(--kc-gold)]/20 text-[var(--kc-gold-dark)]" : "bg-[var(--kc-navy)]/10 text-[var(--kc-navy)]";
};

export const MonthCalendar = ({ rdvs, onEdit }) => {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Date());
  const days = useMemo(() => eachDayOfInterval({ start: startOfWeek(month, { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) }), [month]);
  const byDay = useMemo(() => rdvs.reduce((acc, r) => { const k = r.date.slice(0, 10); (acc[k] = acc[k] || []).push(r); return acc; }, {}), [rdvs]);
  const selectedItems = (byDay[key(selected)] || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  const goToday = () => { setMonth(startOfMonth(new Date())); setSelected(new Date()); };

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_360px]" data-testid="month-calendar">
      <section className="kc-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold capitalize text-[var(--kc-navy)]" data-testid="calendar-month-label">{format(month, "MMMM yyyy", { locale: fr })}</h2>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today-button">Aujourd'hui</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth((m) => subMonths(m, 1))} data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 border-b border-[var(--kc-line)] pb-2 text-center">
          {WEEKDAYS.map((w) => <span key={w} className="kc-eyebrow">{w}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-px bg-[var(--kc-line)] border border-[var(--kc-line)] mt-2 rounded-lg overflow-hidden">
          {days.map((d) => {
            const items = (byDay[key(d)] || []).slice().sort((a, b) => a.date.localeCompare(b.date));
            const inMonth = isSameMonth(d, month);
            const isSel = isSameDay(d, selected);
            return (
              <button key={key(d)} type="button" onClick={() => setSelected(d)} data-testid={`calendar-day-${key(d)}`}
                className={`flex min-h-[96px] flex-col items-stretch p-1.5 text-left transition-colors ${inMonth ? "bg-white" : "bg-[var(--kc-bg)]"} ${isSel ? "ring-2 ring-inset ring-[var(--kc-navy)]" : "hover:bg-[var(--kc-sand)]/60"}`}>
                <span className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday(d) ? "bg-[var(--kc-gold)] text-[var(--kc-navy)]" : inMonth ? "text-[var(--kc-navy)]" : "text-[var(--kc-muted)]/60"}`}>{format(d, "d")}</span>
                <div className="space-y-0.5">
                  {items.slice(0, 3).map((r) => (
                    <p key={r.id} className={`truncate rounded px-1 py-0.5 text-[11px] leading-tight ${pillClass(r)}`} title={r.titre}>
                      <span className="font-mono">{r.date.slice(11, 16)}</span> {r.titre}
                    </p>
                  ))}
                  {items.length > 3 && <p className="px-1 text-[10px] text-[var(--kc-muted)]">+{items.length - 3} autre(s)</p>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-[var(--kc-muted)]">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[var(--kc-navy)]/30" />Rendez-vous</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[var(--kc-gold)]/60" />Relance</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#B42318]/40" />En retard</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[var(--kc-sand)]" />Traité</span>
        </div>
      </section>
      <aside className="kc-card p-5" data-testid="calendar-selected-panel">
        <p className="kc-eyebrow mb-1">Détail du jour</p>
        <h3 className="font-display text-lg font-semibold capitalize text-[var(--kc-navy)]" data-testid="calendar-selected-label">{format(selected, "EEEE d MMMM", { locale: fr })}</h3>
        <div className="mt-4 space-y-2.5" data-testid="calendar-selected-list">
          {selectedItems.map((r) => <RdvItem key={r.id} rdv={r} onEdit={onEdit} />)}
          {!selectedItems.length && <Empty>Rien de prévu ce jour-là.</Empty>}
        </div>
      </aside>
    </div>
  );
};
